import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useInspectionStore } from '@/context/inspectionStore';
import { useCurrentUser } from '@/components/auth/AuthProvider';
import { ClipboardCheck, Calendar, CalendarDays, Lock, AlertTriangle, Clock } from 'lucide-react';
import { getVehicleSchedule, getOverdueStatus } from '@/services/scheduleService';
import type { InspectionType } from '@/types';
import type { VehicleSchedule, DueStatus } from '@/services/scheduleService';

const INSPECTION_TYPES: {
    type: InspectionType;
    label: string;
    description: string;
    icon: React.ReactNode;
    checks: string[];
}[] = [
        {
            type: 'daily',
            label: 'Daily Inspection',
            description: 'Quick exterior and equipment check',
            icon: <ClipboardCheck className="h-8 w-8" />,
            checks: ['Exterior & body', 'Lights & signals', 'Tires', 'Cargo & equipment'],
        },
        {
            type: 'weekly',
            label: 'Weekly Inspection',
            description: 'All daily checks plus mechanical systems',
            icon: <Calendar className="h-8 w-8" />,
            checks: ['All daily checks', 'Fluids & leaks', 'Brakes & steering', 'Interior & cab', 'Tire pressure'],
        },
        {
            type: 'monthly',
            label: 'Monthly Inspection',
            description: 'Comprehensive full vehicle inspection',
            icon: <CalendarDays className="h-8 w-8" />,
            checks: ['All weekly checks', 'Fluid levels', 'Safety equipment', 'Registration & insurance', 'Spare tire'],
        },
    ];

// Priority order: monthly > weekly > daily
const TYPE_PRIORITY: Record<InspectionType, number> = {
    monthly: 3,
    weekly: 2,
    daily: 1,
};

// Status priority: overdue > due-today > upcoming > unknown
const STATUS_PRIORITY: Record<DueStatus, number> = {
    overdue: 3,
    'due-today': 2,
    upcoming: 1,
    unknown: 0,
};

/**
 * Determines the required inspection type based on schedule.
 * Rules:
 * - If no records exist, monthly is required
 * - Overdue takes priority over due-today
 * - Among same status, higher level inspection takes priority
 * - User must complete the highest priority required inspection
 */
function getRequiredInspectionType(
    schedule: VehicleSchedule | null
): { required: InspectionType; reason: string } | null {
    if (!schedule) return { required: 'monthly', reason: 'No inspection records found' };

    const statuses: { type: InspectionType; status: DueStatus; nextDue: string | undefined }[] = [
        { type: 'monthly', status: getOverdueStatus(schedule.nextMonthlyDue), nextDue: schedule.nextMonthlyDue },
        { type: 'weekly', status: getOverdueStatus(schedule.nextWeeklyDue), nextDue: schedule.nextWeeklyDue },
        { type: 'daily', status: getOverdueStatus(schedule.nextDailyDue), nextDue: schedule.nextDailyDue },
    ];

    // Find the highest priority inspection that needs attention (overdue or due-today)
    const needsAttention = statuses.filter(s => s.status === 'overdue' || s.status === 'due-today');

    if (needsAttention.length === 0) return null; // all upcoming — free choice

    // Sort by: status priority desc, then type priority desc
    needsAttention.sort((a, b) => {
        const statusDiff = STATUS_PRIORITY[b.status] - STATUS_PRIORITY[a.status];
        if (statusDiff !== 0) return statusDiff;
        return TYPE_PRIORITY[b.type] - TYPE_PRIORITY[a.type];
    });

    const top = needsAttention[0];
    const reason = top.status === 'overdue'
        ? `${top.type.charAt(0).toUpperCase() + top.type.slice(1)} inspection is overdue`
        : `${top.type.charAt(0).toUpperCase() + top.type.slice(1)} inspection is due today`;

    return { required: top.type, reason };
}

export function InspectionTypePage() {
    const navigate = useNavigate();
    const user = useCurrentUser();
    const inspection = useInspectionStore(s => s.activeInspection);
    const startInspection = useInspectionStore(s => s.startInspection);

    const [schedule, setSchedule] = React.useState<VehicleSchedule | null>(null);
    const [loadingSchedule, setLoadingSchedule] = React.useState(true);

    React.useEffect(() => {
        if (!inspection) navigate('/');
    }, [inspection, navigate]);

    // Load vehicle schedule
    React.useEffect(() => {
        if (!inspection?.vehicleId) return;
        getVehicleSchedule(inspection.vehicleId)
            .then(s => setSchedule(s))
            .catch(() => setSchedule(null))
            .finally(() => setLoadingSchedule(false));
    }, [inspection?.vehicleId]);

    if (!inspection) return null;

    const requirement = !loadingSchedule ? getRequiredInspectionType(schedule) : null;
    const requiredType = requirement?.required ?? null;

    // A type is locked if there's a required type and this type is lower priority
    function isLocked(type: InspectionType): boolean {
        if (!requiredType) return false;
        return TYPE_PRIORITY[type] < TYPE_PRIORITY[requiredType];
    }

    // A type is the required one
    function isRequired(type: InspectionType): boolean {
        return requiredType === type;
    }

    function getStatusBadge(type: InspectionType): { label: string; color: string } | null {
        if (!schedule) return null;
        const nextDue = type === 'daily' ? schedule.nextDailyDue
            : type === 'weekly' ? schedule.nextWeeklyDue
            : schedule.nextMonthlyDue;
        const status = getOverdueStatus(nextDue);
        if (status === 'overdue') return { label: 'Overdue', color: 'text-status-fail bg-status-fail/10' };
        if (status === 'due-today') return { label: 'Due Today', color: 'text-status-warning bg-status-warning/10' };
        return null;
    }

    function handleSelect(type: InspectionType) {
        if (!user || !inspection) return;
        if (isLocked(type)) return;

        // If an inspection of this type is already in progress for this vehicle,
        // don't call startInspection — that would wipe all existing photos and checks.
        // Just navigate directly to the photos page.
        if (
            inspection.type === type &&
            inspection.vehicleId === inspection.vehicleId &&
            inspection.status === 'in-progress'
        ) {
            navigate('/inspect/photos');
            return;
        }

        const expectedVehicleDescription = useInspectionStore.getState().expectedVehicleDescription ?? undefined;

        startInspection(
            {
                id: inspection.vehicleId,
                unitNumber: inspection.vehicleUnitNumber,
                vin: '',
                year: 0,
                make: '',
                model: '',
                licensePlate: '',
                state: '',
                mileage: inspection.mileage,
                status: 'active',
            },
            type,
            { id: user.id, name: user.name, email: user.email, role: user.role },
            expectedVehicleDescription,
        );

        navigate('/inspect/photos');
    }

    return (
        <div className="mx-auto max-w-lg px-4 py-6 dark:bg-gray-800 min-h-full">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-wire3-navy dark:text-wire3-green">Inspection Type</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Select the type of inspection for{' '}
                    <span className="font-semibold text-wire3-navy dark:text-wire3-green">
                        {inspection.vehicleUnitNumber}
                    </span>
                </p>
            </div>

            {/* Required inspection banner */}
            {!loadingSchedule && requirement && (
                <div className={`mb-5 flex items-start gap-3 rounded-xl px-4 py-3 ${
                    getOverdueStatus(
                        requirement.required === 'daily' ? schedule?.nextDailyDue :
                        requirement.required === 'weekly' ? schedule?.nextWeeklyDue :
                        schedule?.nextMonthlyDue
                    ) === 'overdue'
                        ? 'bg-status-fail/10 border border-status-fail/20'
                        : 'bg-status-warning/10 border border-status-warning/20'
                }`}>
                    <AlertTriangle className={`h-5 w-5 shrink-0 mt-0.5 ${
                        getOverdueStatus(
                            requirement.required === 'daily' ? schedule?.nextDailyDue :
                            requirement.required === 'weekly' ? schedule?.nextWeeklyDue :
                            schedule?.nextMonthlyDue
                        ) === 'overdue' ? 'text-status-fail' : 'text-status-warning'
                    }`} />
                    <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                            {requirement.reason}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            You must complete the {requirement.required} inspection before proceeding.
                        </p>
                    </div>
                </div>
            )}

            {/* No inspections due banner */}
            {!loadingSchedule && !requirement && schedule && (
                <div className="mb-5 flex items-start gap-3 rounded-xl px-4 py-3 bg-status-pass/10 border border-status-pass/20">
                    <Clock className="h-5 w-5 shrink-0 mt-0.5 text-status-pass" />
                    <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                            All inspections are up to date
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            No inspections are due today. You may still complete any inspection type.
                        </p>
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {INSPECTION_TYPES.map(({ type, label, description, icon, checks }) => {
                    const locked = isLocked(type);
                    const required = isRequired(type);
                    const badge = getStatusBadge(type);

                    return (
                        <button
                            key={type}
                            onClick={() => handleSelect(type)}
                            disabled={locked}
                            className={`card dark:bg-gray-750 dark:border-gray-700 flex flex-col gap-3 text-left transition-all ${
                                locked
                                    ? 'opacity-40 cursor-not-allowed'
                                    : required
                                    ? 'border-status-warning dark:border-status-warning shadow-md hover:shadow-lg'
                                    : 'hover:shadow-md hover:border-wire3-green dark:hover:border-wire3-green active:scale-[0.99]'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${
                                    locked
                                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                                        : required
                                        ? 'bg-status-warning/10 text-status-warning'
                                        : 'bg-wire3-navy/5 dark:bg-wire3-green/10 text-wire3-navy dark:text-wire3-green'
                                }`}>
                                    {locked ? <Lock className="h-6 w-6" /> : icon}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className={`font-bold ${
                                            locked ? 'text-gray-400' :
                                            required ? 'text-status-warning' :
                                            'text-wire3-navy dark:text-wire3-green'
                                        }`}>{label}</p>
                                        {badge && !locked && (
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.color}`}>
                                                {badge.label}
                                            </span>
                                        )}
                                        {required && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-status-warning/10 text-status-warning">
                                                Required
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
                                </div>
                            </div>

                            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                                    Includes
                                </p>
                                <div className="grid grid-cols-2 gap-1">
                                    {checks.map(check => (
                                        <div key={check} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                                            <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${locked ? 'bg-gray-300' : 'bg-wire3-green'}`} />
                                            {check}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {locked && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                    Complete the {requiredType} inspection first
                                </p>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
